import type { ClinicalImpression, Annotation as FhirAnnotation } from "@medplum/fhirtypes";
import { CLINICAL_NOTE_ENTITY_ID_SYSTEM, getTRPCClient, VISIT_NOTE_CODE } from "@repo/api";
import {
  type CreateNoteInput,
  clinicalNoteIdInputSchema,
  clinicalNoteIdSchema,
  createNoteInputSchema,
  type NoteIdInput,
  noteCategorySchema,
  patientIdSchema,
  practitionerIdSchema,
  updateNoteInputSchema,
  visitIdSchema,
} from "@repo/api/schemas";
import {
  NOTE_KINDS,
  type NoteCategory,
  type NoteId,
  type PatientId,
  type PractitionerId,
  type VisitId,
} from "@repo/domain";
import { NOTE_CATEGORY_URL, NOTE_VOIDED_FROM_URL } from "@repo/fhir-systems/extensions";
import { and, eq, useLiveQuery } from "@tanstack/react-db";
import { defineWrite } from "../actions/define-write";
import { useCollection } from "../core/hooks";
import { getDataStoreClient } from "../core/provider";
import { DomainResultError } from "../core/write-errors";
import type { ClinicalImpressionRow } from "../fhir/types";
import { optimisticPatientReference, patientDomainId } from "./_patient-reference";
import { practitionerDomainId } from "./_practitioner-reference";
import { optimisticVisitExecutionReference, visitDomainId } from "./_visit-reference";
import type { Annotation, CodeableConcept, Period, Ref } from "./shared/types";

const FINDING_ITEM_TYPES = new Set(["Condition", "Observation", "DiagnosticReport", "RiskAssessment"]);
type FindingItemType = "Condition" | "Observation" | "DiagnosticReport" | "RiskAssessment";

/**
 * Note — normalized read-model over the ClinicalImpression row.
 * Findings reference the source observation/condition via `itemRef`.
 */
export interface NoteDto {
  noteId: NoteId;
  patientId: PatientId;
  visitId?: VisitId;

  status: "in-progress" | "completed" | "entered-in-error";
  code?: CodeableConcept;

  assessorPractitionerId?: PractitionerId;
  date?: string;
  effectiveDateTime?: string;
  effectivePeriod?: Period;

  /** The note's title. */
  description?: string;
  /** What the note is about, from the fixed list. */
  category?: NoteCategory;
  summary?: string;

  findings?: Array<{
    itemCodeableConcept?: CodeableConcept;
    itemRef?: Ref<FindingItemType>;
    basis?: string;
  }>;

  problemConditionIds?: string[];

  notes?: Annotation[];
}

type Finding = NonNullable<NoteDto["findings"]>[number];

const CLINICAL_IMPRESSION_COLLECTION = "clinicalImpression";

function flatCodeable(
  c: { coding?: Array<{ system?: string; code?: string; display?: string }>; text?: string } | undefined,
): CodeableConcept | undefined {
  if (!c) return undefined;
  const first = c.coding?.[0];
  const out = {
    ...(first?.system ? { system: first.system } : {}),
    ...(first?.code ? { code: first.code } : {}),
    ...(first?.display ? { display: first.display } : {}),
    ...(c.text ? { text: c.text } : {}),
  };
  return Object.keys(out).length ? out : undefined;
}

function flatPeriod(p: { start?: string; end?: string } | undefined): Period | undefined {
  if (!p) return undefined;
  const out = {
    ...(p.start ? { start: p.start } : {}),
    ...(p.end ? { end: p.end } : {}),
  };
  return Object.keys(out).length ? out : undefined;
}

function refId(ref: string | undefined, type: string): string | undefined {
  if (!ref?.startsWith(`${type}/`)) return undefined;
  return ref.slice(type.length + 1);
}

function refIdsByType(refs: Array<{ reference?: string }> | undefined, type: string): string[] | undefined {
  if (!refs?.length) return undefined;
  const ids = refs.map((r) => refId(r.reference, type)).filter((id): id is string => !!id);
  return ids.length ? ids : undefined;
}

function parseFindingItemRef(ref: string | undefined): Ref<FindingItemType> | undefined {
  if (!ref) return undefined;
  const [type, id] = ref.split("/");
  if (!type || !id || !FINDING_ITEM_TYPES.has(type)) return undefined;
  return { resourceType: type as FindingItemType, id };
}

function normalizeFindings(findings: ClinicalImpression["finding"] | undefined): Finding[] | undefined {
  if (!findings?.length) return undefined;
  const mapped: Finding[] = [];
  for (const f of findings) {
    const itemCodeableConcept = flatCodeable(f.itemCodeableConcept);
    const itemRef = parseFindingItemRef(f.itemReference?.reference);
    if (!itemCodeableConcept && !itemRef && !f.basis) continue;
    mapped.push({
      ...(itemCodeableConcept ? { itemCodeableConcept } : {}),
      ...(itemRef ? { itemRef } : {}),
      ...(f.basis ? { basis: f.basis } : {}),
    });
  }
  return mapped.length ? mapped : undefined;
}

function parseAuthorRef(ref: string | undefined): Annotation["authorRef"] | undefined {
  if (!ref) return undefined;
  const [type, id] = ref.split("/");
  const allowed = new Set(["Practitioner", "Patient", "RelatedPerson", "Organization"]);
  if (!type || !id || !allowed.has(type)) return undefined;
  return { resourceType: type, id } as Annotation["authorRef"];
}

function normalizeNotes(notes: FhirAnnotation[] | undefined): Annotation[] | undefined {
  if (!notes?.length) return undefined;
  return notes.map((n) => ({
    text: n.text ?? "",
    ...(n.time ? { time: n.time } : {}),
    ...(parseAuthorRef(n.authorReference?.reference)
      ? { authorRef: parseAuthorRef(n.authorReference?.reference) }
      : {}),
  }));
}

// A code outside the list is dropped rather than shown: the server refuses to
// hydrate such a row, so it is not a category anyone can have picked.
export function parseCategory(extensions: ClinicalImpression["extension"]): NoteCategory | undefined {
  const parsed = noteCategorySchema.safeParse(extensions?.find((e) => e.url === NOTE_CATEGORY_URL)?.valueCode);
  return parsed.success ? parsed.data : undefined;
}

export function categoryExtensions(
  prior: ClinicalImpression["extension"],
  category: NoteCategory | undefined,
): ClinicalImpression["extension"] {
  const others = (prior ?? []).filter((e) => e.url !== NOTE_CATEGORY_URL);
  const next = category ? [...others, { url: NOTE_CATEGORY_URL, valueCode: category }] : others;
  return next.length ? next : undefined;
}

function toDto(ci: ClinicalImpressionRow): NoteDto | undefined {
  // A note written before ADR-0020 has no identity to project. Skip it rather
  // than parse `undefined` and take the whole live query down with it.
  if (!ci.domainId) return undefined;
  const noteId = clinicalNoteIdSchema.safeParse(ci.domainId);
  const patientId = patientIdSchema.safeParse(patientDomainId(ci.subject));
  const assessorId = practitionerIdSchema.safeParse(practitionerDomainId(ci.assessor));
  const visitId = visitIdSchema.safeParse(visitDomainId(ci.encounter));
  if (!noteId.success || !patientId.success) return undefined;
  return {
    noteId: noteId.data,
    patientId: patientId.data,
    ...(visitId.success ? { visitId: visitId.data } : {}),
    status: (ci.status ?? "in-progress") as NoteDto["status"],
    ...(flatCodeable(ci.code) ? { code: flatCodeable(ci.code) } : {}),
    ...(assessorId.success ? { assessorPractitionerId: assessorId.data } : {}),
    ...(ci.date ? { date: ci.date } : {}),
    ...(ci.effectiveDateTime ? { effectiveDateTime: ci.effectiveDateTime } : {}),
    ...(flatPeriod(ci.effectivePeriod) ? { effectivePeriod: flatPeriod(ci.effectivePeriod) } : {}),
    ...(ci.description ? { description: ci.description } : {}),
    ...(parseCategory(ci.extension) ? { category: parseCategory(ci.extension) } : {}),
    ...(ci.summary ? { summary: ci.summary } : {}),
    ...(normalizeFindings(ci.finding) ? { findings: normalizeFindings(ci.finding) } : {}),
    ...(refIdsByType(ci.problem, "Condition") ? { problemConditionIds: refIdsByType(ci.problem, "Condition") } : {}),
    ...(normalizeNotes(ci.note) ? { notes: normalizeNotes(ci.note) } : {}),
  };
}

interface UseNotesParams {
  visitId?: VisitId;
  patientId?: string;
}

interface UseNotesResult {
  notes: NoteDto[];
  isLoading: boolean;
  isReady: boolean;
  isError: boolean;
}

export function useNotes(params?: UseNotesParams): UseNotesResult {
  const collection = useCollection(CLINICAL_IMPRESSION_COLLECTION);
  const visitId = params?.visitId;
  const patientId = params?.patientId;

  const result = useLiveQuery({
    queryKey: [collection.id, "notes", visitId ?? null, patientId ?? null],
    query: (q) => {
      const base = q.from({ ci: collection });
      if (visitId) {
        return base
          .where(({ ci }) => eq(ci.visitId, visitId))
          .fn.select(({ ci }) => ({
            dto: toDto(ci),
          }));
      }
      if (patientId) {
        return base
          .where(({ ci }) => eq(ci.subject?.identifier?.value, patientId))
          .fn.select(({ ci }) => ({
            dto: toDto(ci),
          }));
      }
      return undefined;
    },
  });

  return {
    notes: ((result.data as Array<{ dto?: NoteDto }> | undefined) ?? []).flatMap((row) => (row.dto ? [row.dto] : [])),
    isLoading: result.isLoading,
    isReady: result.isReady,
    isError: result.isError,
  };
}

/** The one note a visit carries, as its editor reads it. */
export interface VisitNoteDto {
  noteId: NoteId;
  text: string;
  title?: string;
  category?: NoteCategory;
}

interface UseVisitNoteResult {
  note: VisitNoteDto | null;
  isReady: boolean;
}

/**
 * The visit's note, on its own feed. Kept off the visit DTO on purpose: the
 * whole visit page renders from that DTO, so a note riding on it re-rendered
 * every procedure card on each autosave.
 */
export function useVisitNote(visitId: string | undefined): UseVisitNoteResult {
  const collection = useCollection(CLINICAL_IMPRESSION_COLLECTION);
  const result = useLiveQuery({
    queryKey: [collection.id, "visit-note", visitId ?? null],
    query: (q) => {
      if (!visitId) return undefined;
      return q
        .from({ ci: collection })
        .where(({ ci }) => and(eq(ci.visitId, visitId), eq(ci.codeCode, VISIT_NOTE_CODE.code)))
        .fn.select(({ ci }) => ({ dto: toVisitNoteDto(ci) }))
        .findOne();
    },
  });
  return {
    note: (result.data as { dto?: VisitNoteDto } | undefined)?.dto ?? null,
    isReady: result.isReady,
  };
}

function toVisitNoteDto(ci: ClinicalImpressionRow): VisitNoteDto | undefined {
  const noteId = clinicalNoteIdSchema.safeParse(ci.domainId);
  if (!noteId.success) return undefined;
  const category = parseCategory(ci.extension);
  return {
    noteId: noteId.data,
    text: ci.note?.[0]?.text ?? "",
    ...(ci.description ? { title: ci.description } : {}),
    ...(category ? { category } : {}),
  };
}

const clinicalImpressions = () => getDataStoreClient().getCollection("clinicalImpression");

/** Rows are keyed by domain identity; this only asserts the row is loaded. */
function resourceIdForDomainId(noteId: string): string {
  if (!clinicalImpressions().has(noteId)) throw new Error(`Clinical note '${noteId}' is not loaded`);
  return noteId;
}

function buildFindingFhir(finding: CreateNoteInput["finding"]): ClinicalImpression["finding"] {
  return (finding ?? []).map((f) => ({
    itemCodeableConcept: f.itemCodeableConcept
      ? {
          coding: f.itemCodeableConcept.coding?.map((c) => ({
            system: c.system,
            code: c.code,
            display: c.display,
          })),
        }
      : undefined,
    itemReference: f.itemReference ? { reference: `${f.itemReference.resourceType}/${f.itemReference.id}` } : undefined,
    basis: f.basis,
  }));
}

const asRow = (ci: ClinicalImpression): ClinicalImpressionRow => ci as ClinicalImpressionRow;

/** Intent: "draft an encounter note (ClinicalImpression)" */
export const draftNote = defineWrite<CreateNoteInput>({
  optimistic: (params) => {
    const input = createNoteInputSchema.parse(params);
    const optimisticNoteId = input.id;
    const record = {
      resourceType: "ClinicalImpression" as const,
      identifier: [{ system: CLINICAL_NOTE_ENTITY_ID_SYSTEM, value: optimisticNoteId }],
      domainId: optimisticNoteId,
      status: "in-progress" as const,
      subject: optimisticPatientReference(input.patient.id),
      encounter: input.visitId ? optimisticVisitExecutionReference(input.visitId) : undefined,
      // The server supplies the authenticated assessor.
      date: input.date,
      code: { coding: [{ system: VISIT_NOTE_CODE.system, code: NOTE_KINDS.patient }] },
      description: input.description,
      summary: input.summary,
      note: input.note ? [{ text: input.note }] : undefined,
      finding: buildFindingFhir(input.finding),
      extension: categoryExtensions(undefined, input.category),
    };
    clinicalImpressions().insert(asRow(record));
  },
  persist: async (params, options) => {
    const result = await getTRPCClient().charting.note.draft.mutate(params, options);
    if (!result.success) throw new DomainResultError(result.error.message);
  },
});

export interface NoteContentInput {
  noteId: NoteId;
  kind: "visit" | "patient";
  title: string;
  text: string;
  category: NoteCategory | null;
}

function noteContentUpdate(input: NoteContentInput) {
  return updateNoteInputSchema.parse({
    noteId: input.noteId,
    description: input.title.trim(),
    category: input.category,
    ...(input.kind === "visit" ? { note: input.text } : { summary: input.text }),
  });
}

export const saveNoteContent = defineWrite<NoteContentInput>({
  // Complete draft snapshots; no status change or server reaction.
  coalesce: true,
  optimistic: (params) => {
    const input = noteContentUpdate(params);
    clinicalImpressions().update(resourceIdForDomainId(input.noteId), (draft) => {
      draft.description = input.description || undefined;
      draft.extension = categoryExtensions(draft.extension, input.category ?? undefined);
      if (input.summary !== undefined) draft.summary = input.summary;
      if (input.note !== undefined) draft.note = input.note ? [{ text: input.note }] : undefined;
    });
  },
  persist: async (params, options) => {
    const result = await getTRPCClient().charting.note.update.mutate(noteContentUpdate(params), options);
    if (!result.success) throw new DomainResultError(result.error.message);
  },
});

/** Intent: "void an encounter note (mark entered-in-error)" */
export const voidNote = defineWrite<NoteIdInput>({
  optimistic: (params) => {
    const input = clinicalNoteIdInputSchema.parse(params);
    clinicalImpressions().update(resourceIdForDomainId(input.noteId), (draft) => {
      const from = draft.status;
      draft.status = "entered-in-error";
      draft.extension = [
        ...(draft.extension ?? []).filter((extension) => extension.url !== NOTE_VOIDED_FROM_URL),
        { url: NOTE_VOIDED_FROM_URL, valueCode: from },
      ];
    });
  },
  persist: async (params, options) => {
    const result = await getTRPCClient().charting.note.void.mutate(params, options);
    if (!result.success) throw new DomainResultError(result.error.message);
  },
});

function recordedVoidedFrom(row: ClinicalImpression | undefined): "in-progress" | "completed" | undefined {
  const code = row?.extension?.find((extension) => extension.url === NOTE_VOIDED_FROM_URL)?.valueCode;
  return code === "in-progress" || code === "completed" ? code : undefined;
}

/** Intent: "undo a void". */
export const reinstateNote = defineWrite<NoteIdInput>({
  optimistic: (params) => {
    const input = clinicalNoteIdInputSchema.parse(params);
    const collection = clinicalImpressions();
    const resourceId = resourceIdForDomainId(input.noteId);
    const existing = collection.get(resourceId);
    const status = recordedVoidedFrom(existing);
    if (!status) throw new DomainResultError("This voided note has no record of its status before the void");
    collection.update(resourceId, (draft) => {
      draft.status = status;
      const extension = draft.extension?.filter((candidate) => candidate.url !== NOTE_VOIDED_FROM_URL);
      draft.extension = extension?.length ? extension : undefined;
    });
  },
  persist: async (params, options) => {
    const result = await getTRPCClient().charting.note.reinstate.mutate(params, options);
    if (!result.success) throw new DomainResultError(result.error.message);
  },
});
